import React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { isChunkLoadError, useChunkAutoReload } from '@bb/ui-next/lib/chunk-reload';
import { routeTree } from './route-tree.gen';
import './styles/public.css';

const shell: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg-base)',
  color: 'var(--ink-3)',
  fontSize: 13,
  fontFamily: 'Pretendard Variable, sans-serif',
};

// Minimal fallback while a lazy chunk loads
function PageLoader() {
  return <div style={shell}>Loading…</div>;
}

// 라우트 렌더 오류·새 배포 뒤 청크 로드 실패 — 공개 사이트는 콘솔 디자인 시스템(@bb/ui-next) 스타일을 안 쓰므로 자체 스타일
function PageError({ error, reset }: { error: Error; reset: () => void }) {
  const chunk = isChunkLoadError(error);
  const reloading = useChunkAutoReload(error);
  const button: React.CSSProperties = {
    padding: '10px 18px', borderRadius: 8, border: '1px solid var(--line-3)', background: 'transparent',
    color: 'var(--ink)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  };
  return (
    <div role="alert" style={{ ...shell, flexDirection: 'column', gap: 12, padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>
        {chunk ? '새 버전이 배포되었습니다' : '페이지를 표시하지 못했습니다'}
      </div>
      <p style={{ margin: 0, lineHeight: 1.7 }}>
        {chunk
          ? (reloading ? '새로고침하고 있습니다…' : '새로고침하면 최신 화면으로 바뀝니다.')
          : '일시적인 문제일 수 있습니다. 새로고침해 주세요.'}
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button type="button" style={{ ...button, background: 'var(--ink)', color: 'var(--bg-base)', borderColor: 'var(--ink)' }} onClick={() => window.location.reload()}>
          새로고침
        </button>
        {!chunk && <button type="button" style={button} onClick={reset}>다시 시도</button>}
      </div>
    </div>
  );
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: PageLoader,
  defaultErrorComponent: PageError,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById('root')!;
createRoot(container).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);
